import { Link } from "react-router-dom"
import {
  HelpCircle, ChevronDown, MessageSquare, GraduationCap, BookOpen,
  CreditCard, Users, Info, ArrowRight, Search,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"

interface FaqItem {
  q: string
  a: string
}

const categories: { name: string; icon: any; tone: string; items: FaqItem[] }[] = [
  {
    name: "Admissions",
    icon: GraduationCap,
    tone: "text-brand-700 dark:text-brand-400",
    items: [
      {
        q: "What are the minimum entry requirements for the NCE programme?",
        a: "Candidates must have at least five (5) O-Level credit passes including English Language and Mathematics, obtained in not more than two sittings. The remaining three credits must be relevant to the chosen teaching subject combination. In addition, candidates must score the minimum JAMB UTME cut-off mark set for Colleges of Education in the year of application.",
      },
      {
        q: "How do I apply for admission to YMS COE?",
        a: "Applications are submitted entirely online through the Student Portal. Begin by creating an account at /portal/register, complete the application form, upload required documents (O-Level results, JAMB result slip, birth certificate, passport photograph), and submit. A non-refundable application fee applies and must be paid before submission.",
      },
      {
        q: "Can I change my course or programme after applying?",
        a: "Yes. Course changes can be requested through the Student Portal before the application deadline. After the deadline, changes are at the discretion of the Admissions Committee and may attract an administrative fee. Approved changes after admission may require additional screening.",
      },
      {
        q: "When does admission open and close each year?",
        a: "Admission cycles typically open in January and close around September, though exact dates vary each session. The current cycle's deadline is displayed on the homepage with a countdown timer. Late applications are not accepted under any circumstances.",
      },
      {
        q: "What is the Pre-NCE / Remedial programme?",
        a: "The Pre-NCE programme is a one-year remedial course designed for candidates who do not meet the direct entry requirements for the NCE. Successful completion with the required grade guarantees progression into the NCE programme. It is ideal for students who narrowly missed the credit requirement or wish to strengthen their foundation.",
      },
    ],
  },
  {
    name: "Academics",
    icon: BookOpen,
    tone: "text-gold-600 dark:text-gold-400",
    items: [
      {
        q: "How long is the NCE programme?",
        a: "The Nigeria Certificate in Education (NCE) is a three-year programme divided into six semesters (NCE 1, 2 and 3). Each academic session comprises two semesters of approximately 18 weeks each, including examinations. Teaching Practice, which is mandatory, is undertaken in the second semester of NCE 2.",
      },
      {
        q: "How are students assessed and graded?",
        a: "Assessment is continuous, combining Continuous Assessment (CA) worth 40% and end-of-semester examinations worth 60%. Grades follow the standard NCE scale: A (70–100, Distinction, 4.0 points), B (60–69.99, Credit, 3.5), C (50–59.99, Merit, 3.0), D (45–49.99, Pass, 2.5), E (40–44.99, Pass, 2.0) and F (below 40, Fail, 0.0).",
      },
      {
        q: "How do I register for courses each semester?",
        a: "Course registration is done online through the Student Portal at the start of each semester. Students select from the approved courses for their level and programme, submit, and print the registration form. Late registration attracts a fee. Only registered students are permitted to sit for examinations.",
      },
      {
        q: "What happens if I fail a course?",
        a: "Failed courses must be repeated in the next available semester the course is offered. A student who fails more than 50% of registered courses in a session may be required to withdraw. Academic advising is available through the department to help struggling students improve.",
      },
      {
        q: "Are there library and laboratory facilities?",
        a: "Yes. The College library holds over 60,000 volumes plus e-journals and digital resources. Each science-based department has dedicated laboratories fully equipped for NCE practicals. The library and labs are open six days a week during session.",
      },
    ],
  },
  {
    name: "Fees & Payments",
    icon: CreditCard,
    tone: "text-brand-700 dark:text-brand-400",
    items: [
      {
        q: "What payment methods are accepted?",
        a: "The College accepts online payments through Paystack, Flutterwave and Monnify. All payments are made via the Student Portal under the Payments section, which generates a unique reference for each transaction. Cash payments at the Bursary are no longer accepted for tuition fees.",
      },
      {
        q: "Can I pay my fees in instalments?",
        a: "Yes, students may pay in up to two instalments per session: 60% at the start of the first semester and the balance before the start of second-semester examinations. The instalment option must be formally requested at the Bursary. Acceptance and registration fees must be paid in full upfront.",
      },
      {
        q: "What should I do if my payment fails but my account is debited?",
        a: "Failed but debited transactions are usually auto-reversed within 24 hours. If after 48 hours the payment has not reflected or been reversed, please contact the Bursary with your transaction reference and bank statement. Provide as much detail as possible to expedite resolution.",
      },
      {
        q: "Are there scholarships or financial aid available?",
        a: "The College, Kano State Government and several private foundations offer scholarships and bursaries to indigent and exceptionally brilliant students. Information is published on the News page and announced through the Student Portal when applications open. External scholarships such as PTDF and NNPC are also publicised.",
      },
      {
        q: "Is the application fee refundable?",
        a: "No, the application fee is non-refundable once payment is confirmed and the application form submitted. This covers administrative and screening costs. Please ensure you meet all requirements before applying.",
      },
    ],
  },
  {
    name: "Student Life",
    icon: Users,
    tone: "text-gold-600 dark:text-gold-400",
    items: [
      {
        q: "Is hostel accommodation available?",
        a: "Yes, the College provides on-campus hostel accommodation for both male and female students on a first-come, first-served basis. Allocation is done after payment of hostel fees and submission of required forms. Off-campus accommodation is also available in nearby communities for those who prefer private housing.",
      },
      {
        q: "What clubs and societies can I join?",
        a: "There are over 20 registered student clubs and societies including the Debate Society, Drama Troupe, Press Club, JETS Club, Mathematics Club, Man 'O' War, Red Cross and various religious fellowships. Students are encouraged to join at least one club to enrich their campus experience.",
      },
      {
        q: "Are there sports facilities on campus?",
        a: "Yes. The College has a standard football pitch, basketball and volleyball courts, table tennis facilities and an indoor games hall. Annual inter-faculty competitions and the NUGA games provide opportunities for talented athletes to represent the College at national level.",
      },
      {
        q: "What health services are available?",
        a: "The College operates a medical centre staffed by qualified doctors and nurses, providing primary healthcare, immunisation, and emergency first aid to students and staff. Serious cases are referred to the partner teaching hospital. Medical insurance is included in the fees.",
      },
    ],
  },
  {
    name: "General",
    icon: Info,
    tone: "text-brand-700 dark:text-brand-400",
    items: [
      {
        q: "How can I verify the authenticity of a certificate, result or transcript?",
        a: "Every official document issued by the College carries a unique QR code. Scanning the code with any smartphone camera opens a verification page that displays the document details in full. You may also visit /verify and enter the verification token manually. Documents without QR codes should be considered suspicious.",
      },
      {
        q: "How do I request an official transcript?",
        a: "Transcript requests are submitted through the Student Portal under the Transcript section. Provide the recipient's name and address, pay the transcript fee, and the document will be dispatched within 5–10 working days. Electronic transcripts are also available and delivered via secure email.",
      },
      {
        q: "Is the College accredited by the NCCE?",
        a: "Yes, all programmes offered at YMS COE are fully accredited by the National Commission for Colleges of Education (NCCE) and relevant professional bodies. The College undergoes periodic re-accreditation to maintain these standards. Accreditation reports are available on request from the Academic Office.",
      },
      {
        q: "How do I reset my Student Portal password?",
        a: "Visit /portal/login and click 'Forgot Password'. Enter your registered email address and a reset link will be sent. The link is valid for 30 minutes. If you no longer have access to your email, contact the ICT support unit in person with valid identification.",
      },
    ],
  },
]

export default function Faq() {
  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "FAQ" }]}
        subtitle="Help Centre"
        title="Frequently Asked Questions"
        description="Find quick answers to common questions about admissions, academics, fees, student life and College services. Use the categories below to navigate."
      />

      {/* Search hint */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card className="border-0 shadow-md bg-gradient-to-br from-brand-50 to-gold-50 dark:from-brand-900/20 dark:to-gold-900/20">
            <CardContent className="pt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-700 grid place-items-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100">Looking for something specific?</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400">Browse by category below or contact us directly if your question isn't listed.</p>
                </div>
              </div>
              <Link to="/contact">
                <Button className="bg-brand-800 dark:bg-brand-700">
                  <MessageSquare className="w-4 h-4 mr-2" /> Contact Us
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Accordion groups */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {categories.map(cat => (
              <div key={cat.name}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-brand-50 dark:bg-brand-900/40 grid place-items-center">
                    <cat.icon className={`w-6 h-6 ${cat.tone}`} />
                  </div>
                  <div>
                    <Badge variant="success" className="mb-1">{cat.items.length} Questions</Badge>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 dark:text-ink-100">{cat.name}</h2>
                  </div>
                </div>

                <div className="space-y-3">
                  {cat.items.map((item, idx) => (
                    <details
                      key={idx}
                      className="group bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-800 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none select-none">
                        <div className="flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
                          <span className="font-semibold text-ink-900 dark:text-ink-100">{item.q}</span>
                        </div>
                        <ChevronDown className="w-5 h-5 text-ink-400 group-open:rotate-180 transition-transform shrink-0" />
                      </summary>
                      <div className="px-5 pb-5 pl-12 text-sm text-ink-600 dark:text-ink-300 leading-relaxed border-t border-ink-100 dark:border-ink-800 pt-4">
                        {item.a}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-14 h-14 rounded-xl bg-gold-500/20 grid place-items-center mx-auto mb-5">
              <MessageSquare className="w-7 h-7 text-gold-300" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Our friendly helpdesk team is available Monday to Friday, 8 AM to 5 PM, to assist with any enquiry not covered in the FAQ. We respond to emails within 48 hours.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact">
                <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">
                  Contact Helpdesk <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/admission-requirements">
                <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">
                  View Admission Requirements
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
